/**
 * Screen mode type for Data Mode toggle configuration.
 * 
 * - 'demo': Toggle disabled, always uses mock data
 * - 'all': Toggle enabled, user can switch between Demo/Live
 */
export type ScreenMode = 'demo' | 'all';

/**
 * Configuration for Data Mode toggle availability per screen.
 * 
 * Key format: For top-level screens use the screen name (e.g., "Customers").
 * For sub-menu screens, concatenate parent and child with underscore (e.g., "Settings_Company").
 */
export const screenModeConfig: Record<string, ScreenMode> = {
  // Top-level screens
  Homepage: 'all',
  Customers: 'all',
  Transactions: 'all',
  My_Tasks: 'all',
  Payments: 'demo',
  Payments_Receipts: 'all',
  Payments_Allocate: 'all',
  Credit_Scoring: 'demo',

  // Collections sub-screens
  Collections_Dashboard: 'all',
  Collections_Collections_Queue: 'all',
  Collections_Promise_to_Pay: 'all',

  // Analytics & Reporting sub-screens
  Analytics_Reporting_Ageing_Analysis: 'all',
  Analytics_Reporting_Multi_Company_Dashboard: 'all',
  Analytics_Reporting_Emailing: 'all',

  // Dispute Management sub-screens
  Dispute_Management_Dashboard: 'all',
  Dispute_Management_All_Disputes: 'all',

  // Settings sub-screens
  Settings_Company: 'all',
  Settings_Users: 'all',
  Settings_General: 'all',
  Settings_Integrations: 'all',
  Settings_Templates: 'all',
  Settings_Collection_Strategies: 'all',
  Settings_Dispute_Types: 'all',
  Settings_AI_Configuration: 'demo',
};

/**
 * Get the screen key from a pathname.
 * Maps URL paths to the screen configuration keys.
 */
export function getScreenKeyFromPath(pathname: string): string | null {
  const pathToKeyMap: Record<string, string> = {
    '/': 'Homepage',
    '/customers': 'Customers',
    '/transactions': 'Transactions',
    '/payments': 'Payments',
    '/payments/receipts': 'Payments_Receipts',
    '/payments/allocate': 'Payments_Allocate',
    '/credit-scoring': 'Credit_Scoring',
    '/tasks': 'My_Tasks',

    // Collections
    '/collections/dashboard': 'Collections_Dashboard',
    '/collections/queue': 'Collections_Collections_Queue',
    '/collections/promises': 'Collections_Promise_to_Pay',

    // Analytics & Reporting
    '/analytics-reporting/ageing-analysis': 'Analytics_Reporting_Ageing_Analysis',
    '/analytics-reporting/multi-company': 'Analytics_Reporting_Multi_Company_Dashboard',
    '/analytics-reporting/emailing': 'Analytics_Reporting_Emailing',

    // Dispute Management
    '/disputes': 'Dispute_Management_Dashboard',
    '/disputes/all': 'Dispute_Management_All_Disputes',

    // Settings
    '/settings/company': 'Settings_Company',
    '/settings/general': 'Settings_General',
    '/users': 'Settings_Users',
    '/settings/users': 'Settings_Users',
    '/integrations': 'Settings_Integrations',
    '/settings/templates': 'Settings_Templates',
    '/settings/strategies': 'Settings_Collection_Strategies',
    '/settings/strategies/create': 'Settings_Collection_Strategies',
    '/settings/strategies/edit': 'Settings_Collection_Strategies',
    '/settings/dispute-types': 'Settings_Dispute_Types',
    '/settings/ai-configuration': 'Settings_AI_Configuration',
  };

  // First try exact match
  if (pathToKeyMap[pathname]) {
    return pathToKeyMap[pathname];
  }

  // For dispute detail pages like /disputes/all/DSP-001, return Dispute_Management_All_Disputes
  if (pathname.startsWith('/disputes/all/')) {
    return 'Dispute_Management_All_Disputes';
  }

  // For customer detail pages like /customers/123, return Customers
  if (pathname.startsWith('/customers/')) {
    return 'Customers';
  }

  // For receipts detail pages like /payments/receipts/123, return Payments_Receipts
  if (pathname.startsWith('/payments/receipts/')) {
    return 'Payments_Receipts';
  }

  // For allocation workspace pages like /payments/allocate/123, return Payments_Allocate
  if (pathname.startsWith('/payments/allocate/')) {
    return 'Payments_Allocate';
  }

  // For multi-company child pages like /analytics-reporting/multi-company/company/1
  if (pathname.startsWith('/analytics-reporting/multi-company')) {
    return 'Analytics_Reporting_Multi_Company_Dashboard';
  }

  // For collections queue child pages like /collections/queue/case/123, return Collections_Collections_Queue
  if (pathname.startsWith('/collections/queue/')) {
    return 'Collections_Collections_Queue';
  }

  // For promise child pages like /collections/promises/create, return Collections_Promise_to_Pay
  if (pathname.startsWith('/collections/promises/')) {
    return 'Collections_Promise_to_Pay';
  }

  // For settings/users child pages like /settings/users/roles/1, return Settings_Users
  if (pathname.startsWith('/settings/users/')) {
    return 'Settings_Users';
  }

  // For strategy child pages like /settings/strategies/123, return Settings_Collection_Strategies
  if (pathname.startsWith('/settings/strategies/')) {
    return 'Settings_Collection_Strategies';
  }

  // For template child pages like /settings/templates/edit, return Settings_Templates
  if (pathname.startsWith('/settings/templates/')) {
    return 'Settings_Templates';
  }

  // For dispute type child pages like /settings/dispute-types/1/workflow, return Settings_Dispute_Types
  if (pathname.startsWith('/settings/dispute-types/')) {
    return 'Settings_Dispute_Types';
  }

  return null;
}

/**
 * Get the screen mode for a given pathname.
 * Returns 'demo' | 'all' based on the screen configuration.
 */
export function getScreenModeForPath(pathname: string): ScreenMode {
  const screenKey = getScreenKeyFromPath(pathname);
  if (screenKey === null) {
    // Unknown screen, default to demo only
    return 'demo';
  }
  return screenModeConfig[screenKey] ?? 'demo';
}
